import React, { useState } from "react";
import { omit } from "lodash";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Alert,
  Button,
  Checkbox,
  CountdownButton,
  Form,
  Input,
  LockIcon,
  toast,
} from "@picklebet/ui";
import { DepositLimits } from "@/features/account/components/DepositLimits";
import { validateProfile } from "../../services/session";
import { toFormErrors, setFormErrors } from "../../utils/errors";
import * as support from "@/utilities/support";
import { useShellStore } from "@/features/shell/stores/shellStore";
import { Verification } from "./Verification";
import type { StoredFormValues } from "./SignUpForm";
import { CustomerLicenceOptions } from "../../stores/authStore";
import { telemetry } from "@/utilities/telemetry";
import { FooterContent } from "./FooterContent";
import { navigate } from "@/utilities/navigate";
import { DEPOSIT_MODAL_PARAM } from "@/features/wallet/components/deposit/utils/params";
import { passwordRulesSchema } from "@/features/auth/models/passwordRules";
import { PasswordRulesField } from "../PasswordRulesField";

import * as styles from "./SignUpForm.module.scss";

const SOUTH_AUSTRALIA_STATE_CODE = "SA";

type IdentityProps = {
  onComplete: (values: FormValues) => Promise<{
    showRetryBetStop?: boolean;
    isExcludedPersonsMatch?: boolean;
  }>;
  storedFormValues?: StoredFormValues;
  promotionCode?: string;
  onInvalidMobileVerification?: (promotionCode?: string) => void;
};

const schema = z
  .object({
    licenceAccepted: z.boolean().refine((val) => val === true, {
      message: "You must agree with terms and conditions",
    }),
    password: passwordRulesSchema,
    showLimits: z.boolean().optional(),
    depositLimit: z.string().optional(),
    depositLimitPeriod: z.string().optional(),
    hasAffiliateCode: z.boolean().optional(),
    promotionCode: z.string().optional(),
    optInToMarketingCommunications: z.boolean().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.showLimits && !data.depositLimit) {
      ctx.addIssue({
        code: "custom",
        message: "Deposit limit is required",
        path: ["depositLimit"],
      });
    }
    if (data.showLimits && !data.depositLimitPeriod) {
      ctx.addIssue({
        code: "custom",
        message: "Deposit limit period is required",
        path: ["depositLimitPeriod"],
      });
    }
  });

type FormValues = z.infer<typeof schema>;

const Identity = ({
  onComplete,
  storedFormValues,
  promotionCode,
  onInvalidMobileVerification,
}: IdentityProps) => {
  const isSouthAustralia =
    storedFormValues?.address?.state === SOUTH_AUSTRALIA_STATE_CODE;

  const countryCode = storedFormValues?.address?.country;
  const isAu = countryCode === "AU";
  const isIsleOfMan =
    storedFormValues?.licence === CustomerLicenceOptions.ISLE_OF_MAN;

  const {
    register,
    unregister,
    handleSubmit,
    formState: { errors, isSubmitting },
    control,
    watch,
    setValue,
    setError,
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      password: "",
      licenceAccepted: false,
      promotionCode: storedFormValues?.promotionCode || promotionCode,
      optInToMarketingCommunications: isSouthAustralia ? false : undefined,
    },
  });

  const setModal = useShellStore((state) => state.setModal);

  const displayAffiliateCodeNotification = (type: "success" | "error") => {
    toast({
      description: `Code "${watch("promotionCode")}" ${
        type === "success" ? "has been entered" : `is invalid`
      }`,
      variant: type === "success" ? "success" : "danger",
    });
  };

  const [isExcludedPersonsMatch, setIsExcludedPersonsMatch] = useState(false);
  const [awaitingVerification, setAwaitingVerification] = useState(false);
  const [validatingPromoCode, setValidatingPromoCode] = useState(false);
  // to show the retry button on betstop error
  const [startCountdown, setStartCountdown] = useState(false);
  const [showBetstopError, setShowBetstopError] = useState(false);

  const showLimits = watch("showLimits", isSouthAustralia);
  const hasAffiliateCode = watch("hasAffiliateCode", !!promotionCode?.length);
  const optInToMarketingCommunications = watch(
    "optInToMarketingCommunications",
    false,
  );
  const formValues = watch();
  const isDisabled = isSubmitting || isExcludedPersonsMatch;

  const onSubmit = async (values: FormValues) => {
    const { password, ...profile } = values;
    // need to re-validate phone
    const [validation] = await validateProfile("", password, {
      ...profile,
      phone: storedFormValues?.phone,
      phoneVerificationRequestId: storedFormValues?.phoneVerificationRequestId,
    });

    if (validation.length > 0) {
      setFormErrors(toFormErrors<FormValues>(validation, "profile"), setError);

      const errorTemplates = new Set(validation.map((e) => e.messageTemplate));

      if (errorTemplates.has("promotion-code")) {
        telemetry.bonusCodeEnteredFe({
          outcome: "failure",
          code: formValues.promotionCode ?? "",
          isSignup: true,
        });
      }

      if (errorTemplates.has("{phone.verification.invalid}")) {
        onInvalidMobileVerification?.(formValues.promotionCode);
      }

      return;
    }

    const result = await onComplete(
      showLimits
        ? values
        : omit(values, ["depositLimit", "depositLimitPeriod"]),
    );

    if (result.showRetryBetStop) {
      setStartCountdown(true);
      setShowBetstopError(true);
      return;
    }

    if (result.isExcludedPersonsMatch) {
      setIsExcludedPersonsMatch(true);
      return;
    }

    // Navigate all Canadian users to the deposit page
    if (countryCode === "CA") {
      navigate(`/wallet/overview?${DEPOSIT_MODAL_PARAM}=true`);
      return;
    }

    // Close the modal for remaining IOM users (NZ)
    if (isIsleOfMan) {
      setModal(null);
      return;
    }

    if (isAu) {
      // for australian users we need to confirm their verification status
      setAwaitingVerification(true);
    } else {
      // for everyone else, we can just close the modal
      setModal(null);
    }
  };

  const handleCountdownReset = () => {
    setStartCountdown(false);
  };

  if (awaitingVerification) {
    return <Verification promotionCode={formValues.promotionCode} />;
  }

  return (
    <>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.loginEmail}>
          <div className={styles.label}>Login Email</div>
          <div className={styles.emailWrapper}>
            {storedFormValues?.details.email}
            <LockIcon className={styles.lock} />
          </div>
        </div>
        <PasswordRulesField
          autoFocus
          id={"password"}
          label="Create Password"
          placeholder="Password"
          control={control}
          name="password"
          errorMessage={errors.password?.message}
          disabled={isDisabled}
        />

        {!isIsleOfMan && (
          <>
            <Checkbox
              containerClassName={styles.checkboxGutter}
              id={"showLimits"}
              label={`I want to set a deposit limit`}
              checked={showLimits}
              onCheckedChange={(checked) =>
                setValue("showLimits", checked === true)
              }
              disabled={isDisabled}
            />
            {showLimits && (
              <DepositLimits
                errors={errors}
                register={register}
                setValue={setValue}
                isExcludedPersonsMatch={isExcludedPersonsMatch}
              />
            )}
          </>
        )}
        <Checkbox
          id={"hasAffiliateCode"}
          label={`I have a code (Optional)`}
          checked={hasAffiliateCode}
          onCheckedChange={(checked) => {
            setValue("hasAffiliateCode", checked === true);
            !checked && unregister("promotionCode");
            telemetry.codeClickedInSignUp({
              checked: !hasAffiliateCode,
            });
          }}
          disabled={isDisabled}
        />
        {isSouthAustralia && (
          <Checkbox
            id={"optInToMarketingCommunications"}
            label={
              <span>
                I agree to receive marketing and{" "}
                <span className="green-link">promotional offers</span> 💰
              </span>
            }
            checked={optInToMarketingCommunications}
            onCheckedChange={(checked) => {
              setValue("optInToMarketingCommunications", checked === true);
            }}
            disabled={isDisabled}
          />
        )}
        {hasAffiliateCode && (
          <div className={styles.affiliate}>
            <Input
              autoFocus
              containerClassName={styles.input}
              placeholder="Enter code"
              {...register("promotionCode")}
              errorMessage={errors.promotionCode?.message}
              disabled={isDisabled}
            />
            <Button
              disabled={!formValues.promotionCode || isDisabled}
              className={styles.button}
              loading={validatingPromoCode}
              size={"md"}
              type="button"
              variant={"blurred"}
              onClick={async () => {
                setValidatingPromoCode(true);
                const [validation] = await validateProfile("", "", {
                  promotionCode: watch("promotionCode", ""),
                });
                const invalidCode = validation.length > 0;
                if (invalidCode) {
                  displayAffiliateCodeNotification("error");
                  setValue("promotionCode", "");
                } else {
                  displayAffiliateCodeNotification("success");
                }
                setValidatingPromoCode(false);
                telemetry.bonusCodeEnteredFe({
                  outcome: invalidCode ? "failure" : "success",
                  code: formValues.promotionCode ?? "",
                  isSignup: true,
                });
              }}
            >
              Apply
            </Button>
          </div>
        )}
        <Controller
          control={control}
          name={"licenceAccepted"}
          render={({ field: { onChange, value, ...field } }) => {
            return (
              <Checkbox
                id={"licenceAccepted"}
                checked={value}
                label={
                  <>
                    I have read and agree with the{" "}
                    <Button
                      variant={`link`}
                      onClick={() =>
                        support.open({
                          labels: ["weblink-terms-and-conditions"],
                        })
                      }
                      disabled={isDisabled}
                    >
                      Terms
                    </Button>
                    {isAu ? ", " : " and "}
                    <Button
                      variant={`link`}
                      onClick={() =>
                        support.open({ labels: ["weblink-privacy-policy"] })
                      }
                      disabled={isDisabled}
                    >
                      Privacy Policy
                    </Button>
                    {isAu && (
                      <>
                        {" "}
                        and{" "}
                        <Button
                          variant={`link`}
                          onClick={() =>
                            support.open({
                              labels: ["web-link-identity-verification-notice"],
                            })
                          }
                          disabled={isDisabled}
                        >
                          Identity Verification Collection Notice
                        </Button>
                      </>
                    )}
                  </>
                }
                hasError={!!errors.licenceAccepted?.message}
                onCheckedChange={(checked) => onChange(checked === true)}
                {...field}
                disabled={isDisabled}
              />
            );
          }}
        />
        {showBetstopError && (
          <Alert color="danger">
            BetStop returned an error. We cannot create your account until we
            receive a successful response from BetStop.
          </Alert>
        )}
        <CountdownButton
          type="submit"
          disabled={isDisabled}
          loading={isSubmitting}
          startCountdown={startCountdown}
          duration={15}
          initialLabel={"Complete"}
          countingLabel={(time: string) => `Retry in ${time}`}
          onExpire={handleCountdownReset}
          finishedLabel={"Retry"}
        />
      </Form>
      <FooterContent className={styles.footer} />
    </>
  );
};

export { Identity };
